export interface HubContent {
  title: string;
  tagline: string;
  description: string;
}

export const hubContent: Record<string, HubContent> = {
  overview: {
    title: 'Solarch Overview & Architecture',
    tagline: 'Foundations of the TypeScript-native Backend-as-a-Service.',
    description: 'Solarch packages SQLite, Express, WebSocket, authentication, and file storage into a single lightweight Node.js dependency. Use the guides below to explore our dual-database architecture, scaffold a new application via the CLI, or follow the step-by-step quickstart tutorial.',
  },
  sdks: {
    title: 'Client SDKs & Protocol Integrations',
    tagline: 'Connect frontend, mobile, desktop, and AI agent applications to your backend.',
    description: 'Solarch provides official client libraries for web browsers, React Native, Electron, and Tauri. For intelligent systems, the AI Agent SDK allows you to orchestrate autonomous workflows, while the Model Context Protocol (MCP) server connects external agent harnesses directly to your database.',
  },
  configuration: {
    title: 'Server Configuration & Environment',
    tagline: 'Fine-tune server runtime settings, directory paths, and secrets.',
    description: 'Solarch operates zero-configuration by default, but can be extensively customized. Use these guides to initialize Solarch programmatically via createServer, configure network ports and database storage paths, and manage environment variables for production security.',
  },
  'api-routing': {
    title: 'Custom API Routing & Express Engine',
    tagline: 'Extend auto-generated collections with custom endpoints and middleware.',
    description: 'Because Solarch runs inside your Node.js process on an underlying Express engine, you are never constrained by auto-generated CRUD. Use these guides to structure custom route handlers, implement reusable controller patterns, attach middleware filters, and validate request payloads.',
  },
  'data-services': {
    title: 'Database Engine & Service Architecture',
    tagline: 'In-process SQLite storage with high concurrency and clean domain logic.',
    description: 'At the heart of Solarch is an embedded SQLite engine powered by better-sqlite3 operating in Write-Ahead Logging (WAL) mode. Explore our 14 schema field types, database connection tuning, and how to structure domain logic cleanly using the injectable service layer pattern.',
  },
  'security-auth': {
    title: 'Identity, Authentication & Security Rules',
    tagline: 'Multi-provider user authentication and declarative access control.',
    description: 'Solarch provides full-featured authentication out of the box, including Email/Password, OAuth2, OTP, MFA/TOTP, and JWT tokens. Use these guides to configure authentication providers and write fine-grained security rules to protect your collections at the row and field level.',
  },
  'hosting-deployment': {
    title: 'Production Hosting & Automated Testing',
    tagline: 'Run Solarch reliably in any Node.js environment with high availability.',
    description: 'As a single-binary Node.js process with zero external database dependencies, Solarch is easy to deploy anywhere. Follow our production hosting guides for Docker, Fly.io, Railway, and bare-metal VPS with PM2, and learn how to run isolated integration tests with Vitest.',
  },
  'file-storage': {
    title: 'File Storage & Media Delivery',
    tagline: 'Handle file uploads with local disk or S3-compatible cloud backends.',
    description: 'Solarch includes a pluggable storage subsystem supporting local filesystem storage as well as S3-compatible cloud providers like AWS S3, Cloudflare R2, and MinIO. Explore guides on handling multipart uploads, serving files, generating on-the-fly thumbnails, and issuing protected download tokens.',
  },
  realtime: {
    title: 'Realtime WebSocket & Event Broadcasting',
    tagline: 'Stream live record changes and custom events to connected clients.',
    description: 'Solarch features a high-throughput realtime subsystem supporting full-duplex WebSockets and unidirectional Server-Sent Events (SSE). Use these guides to configure client subscriptions, manage topic channels, and understand connection lifecycle and automatic cleanup.',
  },
  migrations: {
    title: 'Schema Migrations & Database Versioning',
    tagline: 'Version-control your schema and data changes with automated rollback.',
    description: 'Manage collection schemas reproducibly across development, staging, and production environments. Learn how Solarch structures migration files in pb_migrations, how to run and verify migrations via the CLI, and how to execute safe rollbacks when needed.',
  },
  'javascript-hooks': {
    title: 'JavaScript Hooks & VM Extensibility',
    tagline: 'Execute sandboxed custom logic during database and HTTP lifecycles.',
    description: 'Intercept database lifecycle events before or after records are created, updated, or deleted using sandboxed JavaScript hooks. Explore available hook event triggers, globals exposed via $app and $db, and real-world recipes for email notifications and audit logging.',
  },
  'api-reference': {
    title: 'HTTP REST API Reference',
    tagline: 'Comprehensive endpoint specifications for programmatic backend interaction.',
    description: 'Every Solarch collection automatically generates a full suite of HTTP REST API endpoints. Use this reference to inspect request headers, authentication endpoints, collection schema definitions, record CRUD operations with filter expressions, and administrative actions.',
  },
};
